import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from "@angular/forms";

import { CustomerService } from "@app/services/net-remit/customer.service";

import { CustomerCleanupConfigurationInfoView } from "@app/shared/models/administration/cleanup/CustomerCleanupConfigurationInfoView";
import { CustomerEntityView } from "@app/shared/models/CustomerEntityView";

import { finalize } from "rxjs/internal/operators/finalize";
import { takeWhile } from "rxjs/operators";
import { default as constantsFile } from '@app/shared/constants.json';

@Component({
    selector: 'app-administration-cleanup-list',
    templateUrl: './automation-cleanup-list.component.html',
    styleUrls: ['./automation-cleanup-list.component.scss'],
    standalone: false
})
export class AutomationCleanupListComponent {

  public customerPlaceholderSelectedByDefault = "Common.Select One Customer";

  public alive: boolean = true;
  public clonedCustomerCleanupConfigurations: Array<CustomerCleanupConfigurationInfoView>;
  public constants: any = constantsFile.constants;
  public copyCustomersList: Array<CustomerEntityView>; //For keeping the original customer data
  public customerIdByDefault: number = -1;
  public customersList: Array<CustomerEntityView>;
  public customerNameByDefault: string = " "; // It must be a space so that when it orders when adding, the new rows will be in the beginning
  public customerPlaceholderSelected: string = this.customerPlaceholderSelectedByDefault;
  public maxRows: number = 5; //11
  public onEdition: boolean; //For disabling buttons on table when a row is being edited
  public previousCustomerId: number;
  public searchValue: string;
  public showTable: boolean = true;

  // These fields are used for sorting and keep the option selected by user
  public orderByKeyByDefault: Object = { field: 'name', order: 1 };
  public orderByKey: Object = this.orderByKeyByDefault;
  public formerOrderByKey: Object = this.orderByKeyByDefault;

  // These fields are used as dinamically way. See below
  public orderName: number = 1;
  public orderRetentionMonths: number = 1;

  @Input() editing: boolean;
  @Input() customerCleanupConfigurations: Array<CustomerCleanupConfigurationInfoView>;

  @Output() isEditingMethod = new EventEmitter<boolean>();
  @Output() isLoadingMethod = new EventEmitter<boolean>();
  @Output() showToastMethod = new EventEmitter<{ isClosabled: boolean, severity: string, summary: string, detail: string }>();
  @Output() updateListModificationMethod = new EventEmitter<boolean>();

  @ViewChild("cleanupListForm") cleanupListForm: NgForm;

  constructor(
    private customerService: CustomerService,
    private chRef: ChangeDetectorRef
  ) {}

  ngOnInit(){
    this.customerService.findAllCustomers({ typeCustomer: this.constants.customerTypes.airline, onlyGNR5: true, onlyActive: true, addAccelya: false })
      .pipe(finalize(() => { this.isLoadingMethod.emit(false) }), takeWhile(() => this.alive))
      .subscribe({
        next: (customers: Array<CustomerEntityView>) => {
          this.customersList = this.removeExistingCustomers(structuredClone(customers));
          this.copyCustomersList = structuredClone(customers);
          this.clonedCustomerCleanupConfigurations = structuredClone(this.customerCleanupConfigurations);
        }, error: () => {
          this.showToast("Generic Errors.Error Generic Onload");
        }
      });
  }

  public addCleanupByCustomer(){
    this.setEmptyCustomer();
    // When adding, we save the ordering to apply it after saving
    this.formerOrderByKey = structuredClone(this.orderByKey);
    this.orderByKey = this.orderByKeyByDefault;
    setTimeout(() => this.isEditingMethod.emit(true), 10);
  }

  public onEditCleanup($event: any, cleanupByCustomer: CustomerCleanupConfigurationInfoView){
    $event.preventDefault();
    this.isEditingMethod.emit(true);
    this.onEdition = true;

    this.customerPlaceholderSelected = cleanupByCustomer.name !== this.customerNameByDefault ? cleanupByCustomer.name : this.customerPlaceholderSelectedByDefault;
    this.updatePreviousCustomerId(cleanupByCustomer.customerId);
    if(!this.getIsAdding()){ // After editing the previousCustomerId
      // We only update the former key if it is editing. Otherwise, the former key must be done before than here (see addCleanupByCustomer method)
      this.formerOrderByKey = structuredClone(this.orderByKey);
    }
  }

  public onSaveCleanup(cleanupByCustomer: CustomerCleanupConfigurationInfoView){
    this.updateListModification();
    this.customerPlaceholderSelected = this.customerPlaceholderSelectedByDefault;
    this.isEditingMethod.emit(false);

    // If previousCustomerId is undefined it is because it is adding but
    // the edit event has been click by code to generate the new row (p-table functionality)
    // let isAdding = this.onEdition && this.previousCustomerId;
    if(this.getIsAdding()){
      this.orderByKey = this.formerOrderByKey;
    } else {
      // User is editing so we include back the previous customer into the list
      this.addCustomerToTheListByCustomerId(this.previousCustomerId);
    }
    this.processCurrentRow(cleanupByCustomer);

    this.removeCustomerFromTheList(cleanupByCustomer.customerId);
    this.onEdition = false;
    this.refreshTable();
  }

  public onCancelCleanup(){
    this.customerPlaceholderSelected = this.customerPlaceholderSelectedByDefault;
    this.isEditingMethod.emit(false);
    this.onEdition = false;
    this.clonedCustomerCleanupConfigurations = structuredClone(this.customerCleanupConfigurations);
    this.orderByKey = this.formerOrderByKey;
    this.refreshTable();
  }

  public onDeleteCleanUp(cleanupByCustomer: CustomerCleanupConfigurationInfoView) {
    this.updateListModification();
    this.customerPlaceholderSelected = this.customerPlaceholderSelectedByDefault;
    this.clonedCustomerCleanupConfigurations.splice(this.getIndexFromClonedCustomerCleanupConfigurationsByCustomerId(cleanupByCustomer.customerId), 1);
    this.customerCleanupConfigurations.splice(this.getIndexFromCustomerCleanupConfigurationsByCustomerId(cleanupByCustomer.customerId), 1);
    this.addCustomerToTheListByCustomerId(cleanupByCustomer.customerId);
    this.refreshTable();
  }

  public showToast(detail: string, severity: string = "error", summary: string = "Error", isClosabled: boolean = false){
    this.showToastMethod.emit({ isClosabled: isClosabled, severity: severity, summary: summary, detail: detail } );
  }

  public updateListModification(){
    if(this.clonedCustomerCleanupConfigurations?.length > 0){
      this.updateListModificationMethod.emit(true);
    }
  }

  /*
  * It sorts by selected field to keep it when table is refreshed
  * */
  public updateSortField(field: string, order: string) {
    this[order] = this[order] === 1 ? -1 : 1
    this.orderByKey = { field: field, order: this[order] };
  }

  ///////// Private methods

  /*
  * It creates a new row in the beginning of the table
  * */
  private setEmptyCustomer(){
    let lastEnabledIndex =  this.clonedCustomerCleanupConfigurations.length === 0 ? 0 :
        Math.max(...this.clonedCustomerCleanupConfigurations.map(prbc => prbc.id)) + 1; // According to the biggest assigned id

    this.clonedCustomerCleanupConfigurations.push({ id: lastEnabledIndex, customerId: this.customerIdByDefault, name: this.customerNameByDefault, shortName: "", retentionMonths: undefined });
    this.refreshTable();

    setTimeout(() => {
      //Once a new row has been created doing pushing into the array,
      // this one is moved to first position due to orderByKeyByDefault and call the click event to modify it
      document.getElementById("edit-1").click();
    }, 100);
  }

  /*
  * It uses the current customerId which has been saved into cloned list which is used into the display
  * */
  private getIndexFromClonedCustomerCleanupConfigurationsByCustomerId(customerId: number){
    return this.clonedCustomerCleanupConfigurations.indexOf(
        this.clonedCustomerCleanupConfigurations.find(c => c.customerId === customerId)
    );
  }

  /*
  * It uses the current customerId which has been saved into main list which is used into the display
  * */
  private getIndexFromCustomerCleanupConfigurationsByCustomerId(customerId: number){
    return this.customerCleanupConfigurations.indexOf(
        this.customerCleanupConfigurations.find(c => c.customerId === customerId)
    );
  }

  /*
  * It uses the previous customerId which has been saved into main list which is sent to api
  * */
  private getIndexFromPreviousCustomer(){
    return this.customerCleanupConfigurations.indexOf(
        this.customerCleanupConfigurations.find(c => c.customerId === this.previousCustomerId)
    );
  }

  /*
  * It keeps the previous customerId to use it to add it back to the used customers list into customers dropdown
  * */
  private updatePreviousCustomerId(customerId: number) {
    if(customerId > this.customerIdByDefault){
      this.previousCustomerId = customerId;
    } else {
      this.previousCustomerId = this.customerIdByDefault;
    }
  }

  private getCustomerNameByCustomerId(customerId: number): string {
    return this.copyCustomersList.find(cust => cust.customerId === customerId)?.name;
  }

  /*
  * Due to display problems, we need to hide and show the table once this one has new positions
  * */
  private refreshTable() {
    this.showTable = false;
    this.chRef.detectChanges();
    this.showTable = true;
    this.chRef.detectChanges();
  }

  /*
  * It includes back the customer previously edited to the used array into customers dropdown
  * */
  private addCustomerToTheListByCustomerId(customerId: number) {
    let customer = this.copyCustomersList.find(cust => cust.customerId === customerId);
    this.customersList.push(customer);
    this.customersList = this.customersList.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  /*
  * It removes the selected customer from the used array into customers dropdown
  * */
  private removeCustomerFromTheList(customerId: number) {
    this.customersList.splice(this.customersList.indexOf(this.customersList.find(cust => cust.customerId === customerId)), 1);
  }

  /*
  * It removes all the existing customers when display starts
  * */
  private removeExistingCustomers(customers: CustomerEntityView[]) {
    let existingCustomersIds = this.customerCleanupConfigurations.map(exc => exc.customerId);
    return customers.filter((cust: CustomerEntityView) => {
      return !existingCustomersIds.includes(cust.customerId);
    });
  }

  /*
  * It takes into account if the current row is being added or edited having a look into the arrays and comparing
  * */
  private processCurrentRow(cleanupByCustomer: CustomerCleanupConfigurationInfoView) {
    let savedIndex = this.getIndexFromPreviousCustomer();
    let savedIndexCloned = this.getIndexFromClonedCustomerCleanupConfigurationsByCustomerId(cleanupByCustomer.customerId);

    cleanupByCustomer.name = this.getCustomerNameByCustomerId(cleanupByCustomer.customerId);
    this.clonedCustomerCleanupConfigurations[savedIndexCloned] = cleanupByCustomer;

    if(savedIndex !== -1){
      //It has an id into the array which is going to be saved, so it is a former row
      this.customerCleanupConfigurations[savedIndex] = { ...this.clonedCustomerCleanupConfigurations[savedIndexCloned] };
    } else {
      this.customerCleanupConfigurations.push({ ...this.clonedCustomerCleanupConfigurations[savedIndexCloned] });
    }
  }

  private getIsAdding() {
    return this.previousCustomerId === this.customerIdByDefault;
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this.alive = false;
  }
}
